import '../../Admin/admin-shared.css'
import './agentEval.css'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PageHeader } from '../../../Components/Common/PageHeader'
import { useCQuery } from '../../../Services/QueryCache'
import { showToast } from '../../../utils/toast'
import { useAvviaRun, useEliminaRun, useRuns } from './api'
import { DialogCosto } from './CostDialog'
import { DrawerDifficolta } from './DifficultyDrawer'
import {
  corpoRun,
  costoRun,
  FILTRI_VUOTI,
  misureTestata,
  nomePredefinito,
  nomeRun,
  nuovaCampagnaId,
  quando,
  runAttiva,
  sommaCosti
} from './Functions'
import { GuidaEsiti } from './GuidaEsiti'
import { Icona } from './Icon'
import { FormLancio } from './LaunchForm'
import { Drawer } from './Overlay'
import { BannerRunAttiva } from './RunBanner'
import { DettaglioRun } from './RunDetail'
import { ElencoRun } from './RunsList'
import { CardRiepilogo } from './SummaryCards'
import { t } from './testi'
import { useModuloLancio } from './useModuloLancio'

const FINESTRE = [
  { valore: '1', testo: () => t.aeUltimoGiorno },
  { valore: '7', testo: () => t.aeUltimi7 },
  { valore: '30', testo: () => t.aeUltimi30 },
  { valore: 'tutto', testo: () => t.aeTutto }
]

/** L'analisi vuota, finché il server non ha risposto. */
const DIFFICOLTA_VUOTA = { scenari: 0, sotto: 0, prove: 0, fallite: 0, voci: [] }

/**
 * «Eval dell'agente»: lancia gli scenari del repo su un environment e ne legge i risultati.
 *
 * L'elenco delle run occupa tutta la larghezza. Il dettaglio di quella scelta (anche
 * nell'indirizzo, `?run=`) si apre in un drawer da destra; il form di lancio si apre con
 * «Nuova run». Gli scenari sono file JSON in
 * `Executor/API/agentEval/scenari/` e si modificano lì. Solo in sviluppo: fuori, il server
 * risponde «non disponibile». Vedi `Executor/API/agentEval/README.md`.
 *
 * Questo file tiene lo stato condiviso e compone le sezioni; ognuna sta in un suo file.
 *
 * @returns {import('react').JSX.Element} La pagina
 */
export function AgentEval() {
  const { data: environments = [] } = useCQuery(['environments'])
  const [filtri, setFiltri] = useState(FILTRI_VUOTI)
  const [epocaFiltri, setEpocaFiltri] = useState(0)
  // Card e scenari in difficoltà. Non è il filtro data della tabella.
  const [finestra, setFinestra] = useState('tutto')
  const [pagina, setPagina] = useState(1)
  // La run appena lanciata: l'elenco la rilegge da solo finché non è conclusa.
  const [lanciata, setLanciata] = useState(null)
  const [ripetendo, setRipetendo] = useState(null)
  const [eliminando, setEliminando] = useState(null)
  const [invio, setInvio] = useState(false)
  const [formAperto, setFormAperto] = useState(false)
  const [costoAperto, setCostoAperto] = useState(false)
  const [difficoltaAperta, setDifficoltaAperta] = useState(false)
  const [guidaAperta, setGuidaAperta] = useState(false)
  const [parametri, setParametri] = useSearchParams()
  const runScelta = parametri.get('run')

  const runsQuery = useRuns({ pagina, filtri, finestra, segue: Boolean(lanciata) })
  const avvia = useAvviaRun()
  const elimina = useEliminaRun()
  const runs = useMemo(() => runsQuery.data?.runs ?? [], [runsQuery.data])

  // L'ultima run conclusa: i suoi scenari sotto soglia sono il preset «falliti».
  const ultimaConclusa = useMemo(
    () => runs.find((run) => run.stato === 'completata' || run.stato === 'fermata'),
    [runs]
  )
  const modulo = useModuloLancio({
    aperto: formAperto,
    ultimaConclusaId: ultimaConclusa?.id ?? null
  })
  const { form, piano, scenariQuery } = modulo

  const runInCorso = runsQuery.data
    ? runsQuery.data.runInCorso
    : (scenariQuery.data?.runInCorso ?? null)
  const inCoda = runsQuery.data?.inCoda ?? scenariQuery.data?.inCoda ?? 0
  const runAttivaNota = runs.find((run) => run.id === runInCorso)

  useEffect(() => {
    if (!lanciata || !runsQuery.data) return
    const sua = runs.find((run) => run.id === lanciata)
    if (sua && !runAttiva(sua)) setLanciata(null)
  }, [lanciata, runs, runsQuery.data])

  const cambiaFiltro = (campo, valore) => {
    setFiltri((prima) => ({ ...prima, [campo]: valore }))
    setPagina(1)
  }
  const azzeraFiltri = () => {
    setFiltri(FILTRI_VUOTI)
    setEpocaFiltri((n) => n + 1)
    setPagina(1)
  }
  const scegliRun = (id) => setParametri({ run: id })
  const chiudiRun = useCallback(() => setParametri({}), [setParametri])
  const apriForm = () => {
    setFormAperto(true)
    // Il form sta sopra l'elenco: lo si porta in vista senza aspettare uno scroll a mano.
    requestAnimationFrame(() =>
      document
        .getElementById('ae-nuova-run')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    )
  }

  const cancella = async (run) => {
    const nome = nomeRun(run) || quando(run.avviataIl)
    if (!window.confirm(`${t.aeEliminaConferma} ${nome}`)) return
    setEliminando(run.id)
    try {
      await elimina(run.id)
      if (runScelta === run.id) chiudiRun()
      if (lanciata === run.id) setLanciata(null)
    } catch (errore) {
      showToast('error', t.aeRunNonEliminata, errore?.message)
    } finally {
      setEliminando(null)
    }
  }

  /**
   * Una run per environment, in ordine: le altre aspettano in coda nell'Executor. Con più
   * environment hanno la stessa campagna, e l'elenco le raggruppa.
   */
  const lancia = async () => {
    setInvio(true)
    const nome = form.nome.trim() || nomePredefinito(form.environmentIds)
    const campagna =
      form.environmentIds.length > 1
        ? { id: nuovaCampagnaId(), nome, environmentIds: form.environmentIds.map(Number) }
        : null
    const avviate = []
    try {
      for (const voce of piano) {
        if (!voce.ids.length) continue
        avviate.push(
          await avvia(
            corpoRun(
              { ...form, nome, environmentId: voce.environmentId, scenari: voce.ids, campagna },
              []
            )
          )
        )
      }
      if (!avviate.length) throw new Error(t.aeNienteDaLanciare)
      setLanciata(avviate[0].id)
      setPagina(1)
      setFormAperto(false)
      scegliRun(avviate[0].id)
      showToast(
        'success',
        campagna ? t.aeCampagnaAvviata : t.aeRunAvviata,
        `${avviate.reduce((somma, a) => somma + (a.progresso?.totali ?? 0), 0)} ${t.aeEsecuzioni}`
      )
    } catch (errore) {
      showToast('error', avviate.length ? t.aeCampagnaParziale : t.aeRunNonAvviata, errore?.message)
    } finally {
      setInvio(false)
    }
  }

  const ripeti = async (corpo) => {
    try {
      const avviata = await avvia(corpo)
      setLanciata(avviata.id)
      setPagina(1)
      scegliRun(avviata.id)
      showToast('success', t.aeRunAvviata, `${avviata.progresso?.totali ?? ''} ${t.aeEsecuzioni}`)
    } catch (errore) {
      showToast('error', t.aeRunNonAvviata, errore?.message)
      throw errore
    }
  }

  if (scenariQuery.isError) {
    return (
      <div className="admin-page--wide">
        <PageHeader title={t.aeTitolo} subtitle={t.aeSottotitolo} />
        <div className="ncard">
          <div className="ncard__body ae-stato-vuoto" role="alert">
            <Icona nome="pi-exclamation-triangle" />
            <p className="ae-stato-vuoto__titolo">{t.aeExecutorNonRaggiungibile}</p>
            <p className="ae-muto">{scenariQuery.error?.message}</p>
            <p className="ae-muto">{t.aeExecutorAiuto}</p>
            <button
              type="button"
              className="nbtn nbtn--ghost nbtn--sm"
              onClick={() => scenariQuery.refetch()}
            >
              <Icona nome="pi-refresh" /> {t.aeRiprova}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const testata = runsQuery.data?.testata
  const misure = misureTestata(testata)
  const difficolta = runsQuery.data?.difficolta ?? DIFFICOLTA_VUOTA
  const costiTotali = testata ? misure.costi : sommaCosti(runs.flatMap((run) => costoRun(run)))

  return (
    <div className="admin-page--wide">
      <PageHeader title={t.aeTitolo} subtitle={t.aeSottotitolo} />

      <div className="ae-barra">
        <div className="ae-periodo" role="group" aria-label={t.aeTutto}>
          {FINESTRE.map(({ valore, testo }) => (
            <button
              key={valore}
              type="button"
              className={`nbtn nbtn--sm ${finestra === valore ? 'nbtn--primary' : 'nbtn--ghost'}`}
              aria-pressed={finestra === valore}
              onClick={() => setFinestra(valore)}
            >
              {testo()}
            </button>
          ))}
        </div>
        <div className="ae-barra__azioni">
          <button
            type="button"
            className="nbtn nbtn--ghost nbtn--sm"
            aria-haspopup="dialog"
            onClick={() => setGuidaAperta(true)}
          >
            <Icona nome="pi-info-circle" /> {t.aeGuidaApri}
          </button>
          <button
            type="button"
            className="nbtn nbtn--primary"
            aria-expanded={formAperto}
            aria-controls="ae-nuova-run"
            onClick={() => (formAperto ? setFormAperto(false) : apriForm())}
          >
            <Icona nome={formAperto ? 'pi-times' : 'pi-plus'} />{' '}
            {formAperto ? t.aeChiudiForm : t.aeNuovaRun}
          </button>
        </div>
      </div>

      {runInCorso ? (
        <BannerRunAttiva
          run={runAttivaNota}
          inCoda={inCoda}
          onSegui={() => scegliRun(runInCorso)}
        />
      ) : null}

      <CardRiepilogo
        testata={testata}
        misure={misure}
        costi={costiTotali}
        difficolta={difficolta}
        numeroRun={runs.length}
        runInCorso={runInCorso}
        inCoda={inCoda}
        onCosto={() => setCostoAperto(true)}
        onAnalisi={() => setDifficoltaAperta(true)}
      />

      {formAperto ? (
        <div id="ae-nuova-run">
          <FormLancio
            modulo={modulo}
            environments={environments}
            misure={misure}
            runs={runs}
            invio={invio}
            onLancia={lancia}
          />
        </div>
      ) : null}

      <ElencoRun
        query={runsQuery}
        runs={runs}
        filtri={filtri}
        epocaFiltri={epocaFiltri}
        onFiltro={cambiaFiltro}
        onAzzeraFiltri={azzeraFiltri}
        environments={environments}
        onNuovaRun={apriForm}
        onPagina={setPagina}
        tabella={{
          runScelta,
          ripetendo,
          eliminando,
          onScegli: scegliRun,
          onRipeti: (id, corpo) => {
            setRipetendo(id)
            ripeti(corpo)
              .catch(() => {})
              .finally(() => setRipetendo(null))
          },
          onElimina: cancella
        }}
      />

      {guidaAperta ? <GuidaEsiti t={t} onClose={() => setGuidaAperta(false)} /> : null}
      {costoAperto ? (
        <DialogCosto costi={costiTotali} onClose={() => setCostoAperto(false)} />
      ) : null}
      {difficoltaAperta ? (
        <DrawerDifficolta difficolta={difficolta} onClose={() => setDifficoltaAperta(false)} />
      ) : null}
      {runScelta ? (
        <Drawer etichetta={t.aeDettaglio} chiudiEtichetta={t.aeChiudiForm} onClose={chiudiRun}>
          <DettaglioRun id={runScelta} onRipeti={ripeti} />
        </Drawer>
      ) : null}
    </div>
  )
}

export default AgentEval
